import { Fragment, useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "../lib/auth.js";
import { formatDateOnly } from "../lib/datetime.js";

interface InvoiceLineRow {
  description: string;
  quantity: number;
  unit_cents: number;
  amount_cents: number;
}

interface InvoiceRow {
  id: string;
  kind: string;
  number: string;
  period_start: string;
  period_end: string;
  subtotal_cents: number;
  gst_cents: number;
  total_cents: number;
  status: string;
  sent_at: string | null;
  /** Set when the invoice is sent; null for a draft. */
  due_on: string | null;
  invoice_lines: InvoiceLineRow[];
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * invoices_select_owner / invoice_lines_select_owner (supabase/migrations/0001_init.sql) only
 * grant read access to OWNERS of a business under the client -- staff see nothing here, which
 * matches the RLS policy rather than something this page enforces itself.
 */
export function Invoices() {
  const { activeBusinessId } = useAuth();
  const [clientId, setClientId] = useState<string | null>(null);
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeBusinessId) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from("businesses")
      .select("client_id")
      .eq("id", activeBusinessId)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        setClientId((data as { client_id: string } | null)?.client_id ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeBusinessId]);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    supabase
      .from("invoices")
      .select("id, kind, number, period_start, period_end, subtotal_cents, gst_cents, total_cents, status, sent_at, due_on, invoice_lines(description, quantity, unit_cents, amount_cents)")
      .eq("client_id", clientId)
      .order("period_start", { ascending: false })
      .then(({ data }) => {
        if (cancelled) return;
        setRows((data ?? []) as unknown as InvoiceRow[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (loading) return <p className="rc-loading">Loading...</p>;

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Invoices</h1>
      <div className="rc-table-scroll"><table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
            <th style={{ padding: "10px 12px" }}>Number</th>
            <th style={{ padding: "10px 12px" }}>Period</th>
            <th style={{ padding: "10px 12px" }}>Kind</th>
            <th style={{ padding: "10px 12px" }}>Total</th>
            <th style={{ padding: "10px 12px" }}>Due</th>
            <th style={{ padding: "10px 12px" }}>Status</th>
            <th style={{ padding: "10px 12px" }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.id}>
              <tr style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td data-label="Number" style={{ padding: "10px 12px" }}>{r.number}</td>
                <td data-label="Period" style={{ padding: "10px 12px" }}>
                  {formatDateOnly(r.period_start)} – {formatDateOnly(r.period_end)}
                </td>
                <td data-label="Kind" style={{ padding: "10px 12px" }}>{r.kind}</td>
                <td data-label="Total" style={{ padding: "10px 12px" }}>{money(r.total_cents)}</td>
                <td data-label="Due" style={{ padding: "10px 12px" }}>{r.due_on ? formatDateOnly(r.due_on) : "-"}</td>
                <td data-label="Status" style={{ padding: "10px 12px" }}>{r.status}</td>
                <td className="rc-actions" style={{ padding: "10px 12px" }}>
                  <button
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    style={{ background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, fontSize: 13 }}
                  >
                    {expanded === r.id ? "Hide" : "Details"}
                  </button>
                </td>
              </tr>
              {expanded === r.id && (
                <tr>
                  <td colSpan={7} style={{ padding: "0 8px 16px" }}>
                    <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--rc-surface-sunken)" }}>
                      <tbody>
                        {r.invoice_lines.map((l, i) => (
                          <tr key={i}>
                            <td style={{ padding: 6 }}>{l.description}</td>
                            <td style={{ padding: 6 }}>{l.quantity}</td>
                            <td style={{ padding: 6 }}>{money(l.unit_cents)}</td>
                            <td style={{ padding: 6, textAlign: "right" }}>{money(l.amount_cents)}</td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={3} style={{ padding: 6, textAlign: "right", color: "var(--rc-text-muted)" }}>
                            Subtotal
                          </td>
                          <td style={{ padding: 6, textAlign: "right" }}>{money(r.subtotal_cents)}</td>
                        </tr>
                        <tr>
                          <td colSpan={3} style={{ padding: 6, textAlign: "right", color: "var(--rc-text-muted)" }}>
                            GST
                          </td>
                          <td style={{ padding: 6, textAlign: "right" }}>{money(r.gst_cents)}</td>
                        </tr>
                        <tr>
                          <td colSpan={3} style={{ padding: 6, textAlign: "right", fontWeight: 600 }}>
                            Total
                          </td>
                          <td style={{ padding: 6, textAlign: "right", fontWeight: 600 }}>{money(r.total_cents)}</td>
                        </tr>
                      </tbody>
                    </table></div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                No invoices yet.
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
    </div>
  );
}
